import { useMemo, useState } from 'react';

import type { ResolvedExtensionMember } from './types';
import styles from './StudentMultiSelect.module.css';

interface StudentMultiSelectProps {
  id?: string;
  label?: string;
  onSelectedUserIdsChange(nextUserIds: string[]): void;
  selectedUserIds: string[];
  students: ResolvedExtensionMember[];
  unknownHashedStudentIds?: string[];
}

function formatStudentName(student: ResolvedExtensionMember) {
  const lastFirst = [student.lastName, student.firstName]
    .filter(Boolean)
    .join(', ');

  return lastFirst || student.preferredEmail || student.userId;
}

function getSearchText(student: ResolvedExtensionMember) {
  return [
    student.firstName,
    student.lastName,
    student.preferredEmail,
    student.userId,
  ]
    .join(' ')
    .toLowerCase();
}

function sortStudents(
  firstStudent: ResolvedExtensionMember,
  secondStudent: ResolvedExtensionMember,
) {
  const lastNameCompare = firstStudent.lastName.localeCompare(
    secondStudent.lastName,
  );

  if (lastNameCompare !== 0) {
    return lastNameCompare;
  }

  const firstNameCompare = firstStudent.firstName.localeCompare(
    secondStudent.firstName,
  );

  if (firstNameCompare !== 0) {
    return firstNameCompare;
  }

  return firstStudent.userId.localeCompare(secondStudent.userId);
}

export function StudentMultiSelect({
  id = 'time-extension-students',
  label = 'Students',
  onSelectedUserIdsChange,
  selectedUserIds,
  students,
  unknownHashedStudentIds = [],
}: StudentMultiSelectProps) {
  const [query, setQuery] = useState('');
  const selectedUserIdSet = useMemo(
    () => new Set(selectedUserIds),
    [selectedUserIds],
  );
  const filteredStudents = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return [...students]
      .sort(sortStudents)
      .filter((student) =>
        normalizedQuery ? getSearchText(student).includes(normalizedQuery) : true,
      );
  }, [query, students]);

  function toggleStudent(userId: string) {
    if (selectedUserIdSet.has(userId)) {
      onSelectedUserIdsChange(
        selectedUserIds.filter((selectedUserId) => selectedUserId !== userId),
      );
      return;
    }

    onSelectedUserIdsChange([...selectedUserIds, userId]);
  }

  const selectedStudents = students.filter((student) =>
    selectedUserIdSet.has(student.userId),
  );

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={`${id}-search`}>
        {label}
      </label>
      <input
        className={styles.search}
        id={`${id}-search`}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search by name or email"
        type="search"
        value={query}
      />

      {selectedStudents.length > 0 || unknownHashedStudentIds.length > 0 ? (
        <div aria-label="Selected students" className={styles.chips} role="group">
          {selectedStudents.map((student) => (
            <span className={styles.chip} key={student.userId}>
              {formatStudentName(student)}
              <button
                aria-label={`Remove ${formatStudentName(student)}`}
                onClick={() => toggleStudent(student.userId)}
                type="button"
              >
                x
              </button>
            </span>
          ))}
          {unknownHashedStudentIds.map((hash) => (
            <span className={styles.chip} key={hash}>
              <span className={styles.unknown}>Student no longer on the roster</span>
            </span>
          ))}
        </div>
      ) : null}

      {/* A group of checkboxes: a listbox may hold only options (#173). */}
      <div aria-label="Roster students" className={styles.options} role="group">
        {filteredStudents.length > 0 ? (
          filteredStudents.map((student) => {
            const name = formatStudentName(student);

            return (
              <label className={styles.option} key={student.userId}>
                <input
                  checked={selectedUserIdSet.has(student.userId)}
                  onChange={() => toggleStudent(student.userId)}
                  type="checkbox"
                />
                <span className={styles.optionText}>
                  <span className={styles.optionName}>{name}</span>
                  <span className={styles.optionEmail}>
                    {student.preferredEmail}
                  </span>
                </span>
              </label>
            );
          })
        ) : (
          <p className={styles.empty}>No matching students.</p>
        )}
      </div>
    </div>
  );
}
